import React, { useEffect, useState, useMemo, Fragment } from 'react';
import styled from '@emotion/styled';
import { useOffers } from '../../../../../contexts/OffersContext';
import { useApp } from '../../../../../contexts/AppContext';
import OneTapSwipeOfferActionButton from './OneTapSwipeOfferActionButton';
import OneTapSwipeOfferSuggestOrCounterButton from './OneTapSwipeOfferSuggestOrCounterButton';
import useModal from '../../../../../hooks/useModal';
import { renderAvatar } from '../../../../../common';
import {
  createNewOffer,
  counterOffer,
  sendSuggestion,
  acceptOffer,
  ignoreOffer,
} from '../../../../../actions/offerActions';
import { unlockOutsideMessage } from '../../../../../actions/mailActions';
import { displayWarnMessage } from '../../../../../actions/notificationActions';
import { clearErrors } from '../../../../../actions/commonActions';
import { counterModalClose } from '../../../../../actions/profileTrackAction';
import OfferCounterOffer from '../../OfferCounterOffer';
import OfferAcceptUnlockV2 from '../../OfferAcceptUnlockV2';
import OfferAcceptMessageV2 from '../../OfferAcceptMessageV2';
import OfferCreateV2 from '../../OfferCreateV2';
import { AccountMaker } from '../../../../../models/Account';
import { mixpanelTrackEvent } from '../../../../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../../../../constants/mixpanel';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../../store';
import { useNavigate } from 'react-router-dom';
import breakpoints from '../../../../../theme/material/breakpoints';
import { MEDIA_SCREEN_HEIGHT_250_920, MEDIA_SCREEN_HEIGHT_ONE_TAP_SWIPE } from '../styles/media';
import useMandatoryWall from '../../../../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';

const ActionContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'center',
});

const OfferActionContainer = styled('div')({
  maxWidth: 'calc(100% - 40px)',
  width: '100%',
  position: 'fixed',
  [breakpoints.between(600, 1000)]: {
    [MEDIA_SCREEN_HEIGHT_250_920]: {
      padding: '0 10px',
      position: 'relative',
      top: 45,
      paddingBottom: 100,
      maxWidth: 'calc(100% - 20px)',
    },
  },
  [breakpoints.between(598, 1024)]: {
    [MEDIA_SCREEN_HEIGHT_ONE_TAP_SWIPE]: {
      padding: '0 10px',
      position: 'relative',
      top: 45,
      paddingBottom: 100,
      maxWidth: 'calc(100% - 20px)',
    },
  },
});

interface Props {
  swipe: (direction: 'left' | 'right') => void;
  pageTitle: any;
  restoreCurrentCard: () => void;
}

const OneTapSwipeOfferActions: React.FC<Props> = ({ swipe, pageTitle, restoreCurrentCard }: Props) => {
  const accountModel = useSelector((state: RootState) => AccountMaker.create(state.profile));
  const errors = useSelector((state: RootState) => state.common.errors);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    offerActionContainerRef,
    oneTapActiveAccount,
    setOneTapOfferFlagVariant,
    calculateDynamicMediaRatio,
    declineBtnRef,
    offerBtnRef,
  } = useOffers();

  const [bottom, setBottom] = useState(0);
  const [showOfferAcceptUnlockModal, setShowOfferAcceptUnlockModal] = useState({
    isOpen: false,
    amount: '',
    creditCost: null,
  });
  const [showOfferAcceptMessageModal, setShowOfferAcceptMessageModal] = useState({
    isOpen: false,
    amount: '',
  });
  const [counterOfferModalOpen, setCounterOfferModalOpen] = useState(false);
  const [showOfferCreateV2Modal, setShowOfferCreateV2Modal] = useState(false);
  const { siteFooterLogoRef } = useApp();
  const { resetModal, confirmDeclineOffer } = useModal();
  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();
  const { showMandatoryWall } = useMandatoryWall();

  const userCanMakeOffer = useMemo(() => accountModel.data.can_make_offer, [accountModel.data.can_make_offer]);

  const isOtherAccountIsAttractive = useMemo(() => {
    return oneTapActiveAccount?.profile.isAttractive();
  }, [oneTapActiveAccount]);

  const otherAccount = useMemo(() => {
    return oneTapActiveAccount?.account?.data;
  }, [oneTapActiveAccount]);

  const otherProfile = useMemo(() => {
    return otherAccount?.profile?.data;
  }, [otherAccount]);

  const currencySymbols = useMemo(() => {
    let currencyInitial;
    let currencyLabelUnicode;

    const currencyOriginal = oneTapActiveAccount?.currency_original?.data;

    if (currencyOriginal) {
      currencyInitial = currencyOriginal.initial;
      currencyLabelUnicode = currencyOriginal.label_unicode;
    }

    return {
      currencyInitial,
      currencyLabel: String.fromCharCode(currencyLabelUnicode),
    };
  }, [oneTapActiveAccount]);

  const offerActionVariant = useMemo(() => {
    if (oneTapActiveAccount?.suggest) {
      return isOtherAccountIsAttractive ? 'offer' : 'request';
    } else {
      return 'accept';
    }
  }, [oneTapActiveAccount, isOtherAccountIsAttractive]);

  const handleCreateNewOffer = formData => {
    return dispatch(createNewOffer(formData, false, null, null, true));
  };

  const handleCounterOffer = formData => {
    return dispatch(counterOffer(formData, false, navigate, null, null, true));
  };

  const handleSuggestion = (profileId, showToastMessage = true) => {
    return dispatch(sendSuggestion(profileId, true, null, null, showToastMessage));
  };

  const handleIgnoreOffer = () => {
    confirmDeclineOffer({
      isGenerous: isOtherAccountIsAttractive,
      username: otherAccount.username,
      onConfirm: () => {
        setOneTapOfferFlagVariant('decline');
        swipe('left');

        dispatch(ignoreOffer(oneTapActiveAccount.hash_id, false, false, null, null));

        mixpanelTrackEvent({
          eventName: EVENT_NAMES.OFFER_DECLINED,
          payload: {
            eventProperties: {
              Amount: oneTapActiveAccount.price_original,
              User: accountModel.data.hash_id,
              Source: isOtherAccountIsAttractive ? '1-Tap Received Requests' : '1-Tap Received Offers',
            },
          },
          otherAccountHashId: oneTapActiveAccount.initiator_hash_id,
        });
        resetModal();
      },
      onCancel: () => {
        restoreCurrentCard();
        resetModal();
      },
    });
  };

  const handleNewOffer = () => {
    showMandatoryWall(
      () => {
        if (!userCanMakeOffer && isOtherAccountIsAttractive) {
          if (accountModel.shouldShowTooManyLockedOffersDeals()) {
            setShowTooManyPendingOfferModal(true);
            setModalArgs({ onCloseCallback: restoreCurrentCard });
            return false;
          }

          if (accountModel.shouldShowTooManySentOffers()) {
            setShowTooManyPendingOfferModal(true);
            setModalArgs({ type: 'sent', onCloseCallback: restoreCurrentCard });
            return false;
          }
        }

        setShowOfferCreateV2Modal(true);
      },
      () => restoreCurrentCard(),
      isOtherAccountIsAttractive ? 'Make Offer' : 'Request Date'
    );
  };

  const handleForceOpenCounterModal = () => {
    showMandatoryWall(
      () => {
        if (!userCanMakeOffer && isOtherAccountIsAttractive) {
          if (accountModel.shouldShowTooManyLockedOffersDeals()) {
            setShowTooManyPendingOfferModal(true);
            setModalArgs({ onCloseCallback: restoreCurrentCard });
            return false;
          }

          if (accountModel.shouldShowTooManySentOffers()) {
            setShowTooManyPendingOfferModal(true);
            setModalArgs({ onCloseCallback: restoreCurrentCard, type: 'sent' });
            return false;
          }
        }

        setCounterOfferModalOpen(true);
      },
      () => undefined,
      'Counter Offer'
    );
  };

  const handleAcceptOffer = () => {
    showMandatoryWall(
      async () => {
        if (!userCanMakeOffer && isOtherAccountIsAttractive) {
          if (oneTapActiveAccount?.suggest && accountModel.shouldShowTooManyLockedOffersDeals()) {
            setShowTooManyPendingOfferModal(true);
            setModalArgs({ onCloseCallback: restoreCurrentCard });
            return false;
          }
        }

        if (isOtherAccountIsAttractive) {
          const result = await dispatch(
            acceptOffer(otherAccount.hash_id, '', false, null, null, true, '1-Tap Received Requests', navigate)
          );

          handleAcceptUnlock(true, oneTapActiveAccount.price_original, result);
        } else {
          handleAttractiveMessage(oneTapActiveAccount.price_original);
        }
      },
      () => restoreCurrentCard(),
      'Accept Offer'
    );
  };

  const handleCommonOneTapSwipe = () => {
    setOneTapOfferFlagVariant('accept');
    swipe('right');
  };

  const onCloseAcceptUnlockModal = () => {
    handleCommonOneTapSwipe();
    setShowOfferAcceptUnlockModal({ isOpen: false, amount: '', creditCost: null });
  };

  const onCloseAcceptMessageModal = () => {
    handleCommonOneTapSwipe();
    setShowOfferAcceptMessageModal({ isOpen: false, amount: '' });

    return dispatch(acceptOffer(otherAccount.hash_id, '', false, null, null, true, '1-Tap Received Offers', navigate));
  };

  const handleAcceptUnlock = (toggle: boolean, amount = '', result: any) => {
    showMandatoryWall(() => {
      setShowOfferAcceptUnlockModal({ isOpen: toggle, amount, creditCost: result.credit_cost });
    });
  };

  const handleAttractiveMessage = (amount = '') => {
    showMandatoryWall(
      () => {
        setShowOfferAcceptMessageModal({ isOpen: true, amount });
      },
      () => restoreCurrentCard(),
      'Accept Offer'
    );
  };

  useEffect(() => {
    const siteFooterLogoEl = siteFooterLogoRef?.current;

    if (siteFooterLogoEl) {
      const siteFooterLogoClRec = siteFooterLogoEl.getBoundingClientRect();
      const OFFER_ACTION_MARGIN_BOTTOM = 15;
      const computeDynamicBottom = window.innerHeight - siteFooterLogoClRec.top + OFFER_ACTION_MARGIN_BOTTOM;

      setBottom(computeDynamicBottom);
    }
  }, [siteFooterLogoRef?.current]);

  useEffect(() => {
    //  when bottom values is changed, we need to recalculate the dynamic media ratio
    if (bottom) {
      calculateDynamicMediaRatio();
    }
  }, [bottom]);

  useEffect(() => {
    window.addEventListener('resize', calculateDynamicMediaRatio);
    return () => {
      window.removeEventListener('resize', calculateDynamicMediaRatio);
    };
  }, []);

  return (
    <OfferActionContainer ref={offerActionContainerRef} style={{ bottom }}>
      <ActionContainer>
        <OneTapSwipeOfferActionButton
          btnRef={declineBtnRef}
          onClick={() => {
            handleIgnoreOffer();
          }}
          variant="decline"
        />
        <OneTapSwipeOfferSuggestOrCounterButton
          variant={oneTapActiveAccount?.suggest ? 'suggest' : 'counter'}
          offerPrice={oneTapActiveAccount?.price_original ?? ''}
          currencySymbols={currencySymbols}
          onClick={() => {
            if (offerActionVariant === 'accept') {
              handleForceOpenCounterModal();
            }
          }}
        />
        <OneTapSwipeOfferActionButton
          btnRef={offerBtnRef}
          onClick={() => {
            if (['offer', 'request'].includes(offerActionVariant)) {
              handleNewOffer();
              return;
            }

            if (offerActionVariant === 'accept') {
              handleAcceptOffer();
              return;
            }
          }}
          variant={offerActionVariant}
        />
      </ActionContainer>
      {otherAccount && (
        <>
          <OfferAcceptUnlockV2
            isModalOpen={showOfferAcceptUnlockModal.isOpen}
            offerAmount={showOfferAcceptUnlockModal.amount}
            avatarSrc={renderAvatar(otherProfile?.avatar?.data?.urls, true)}
            username={otherAccount.username}
            buttonID="interestedOffers"
            currentUserIsGenerous={isOtherAccountIsAttractive}
            creditCost={showOfferAcceptUnlockModal.creditCost}
            hashId={otherAccount.hash_id}
            closeModal={onCloseAcceptUnlockModal}
            currentCredit={accountModel.data.credits}
            unlockMessage={(hashId, triggerToBuy, callback = () => undefined) =>
              dispatch(unlockOutsideMessage(hashId, triggerToBuy, callback, isOtherAccountIsAttractive, navigate))
            }
            currencySymbol={currencySymbols}
          />

          <OfferAcceptMessageV2
            isModalOpen={showOfferAcceptMessageModal.isOpen}
            offerAmount={showOfferAcceptMessageModal.amount}
            avatarSrc={renderAvatar(otherProfile?.avatar?.data?.urls, true)}
            username={otherAccount.username}
            buttonID="interestedOffers"
            closeModal={onCloseAcceptMessageModal}
            currencySymbols={currencySymbols}
            onSubmit={(dealMessage: string) => {
              handleCommonOneTapSwipe();
              setShowOfferAcceptMessageModal({ isOpen: false, amount: '' });

              return dispatch(
                acceptOffer(otherAccount.hash_id, dealMessage, false, null, null, true, pageTitle, navigate)
              );
            }}
          />

          <OfferCounterOffer
            isModalOpen={counterOfferModalOpen}
            avatarSrc={renderAvatar(otherProfile?.avatar?.data?.urls, true)}
            offer={{
              data: oneTapActiveAccount,
            }}
            errors={errors}
            closeModal={() => {
              counterModalClose();
              setCounterOfferModalOpen(false);
            }}
            currentUserIsGenerous={isOtherAccountIsAttractive}
            clearErrors={() => undefined}
            username={otherAccount.username}
            counterOffer={handleCounterOffer}
            hashId={otherAccount.hash_id}
            buttonID="counterOffer"
            successOpen={() => {
              setOneTapOfferFlagVariant('counter');
              swipe('left');
            }}
            mixpanelEventSource={isOtherAccountIsAttractive ? '1-Tap Received Requests' : '1-Tap Received Offers'}
          />

          <OfferCreateV2
            isModalOpen={showOfferCreateV2Modal}
            avatarSrc={renderAvatar(otherProfile?.avatar?.data?.urls, true)}
            username={otherAccount.username}
            hashId={otherAccount.hash_id}
            clearErrors={() => dispatch(clearErrors())}
            createNewOffer={handleCreateNewOffer}
            successOpen={() => {
              setOneTapOfferFlagVariant(isOtherAccountIsAttractive ? 'offer' : 'request');
              swipe('right');
            }}
            counterOffer={handleCounterOffer}
            isCounter={false}
            closeModal={() => {
              setShowOfferCreateV2Modal(false);
              restoreCurrentCard();
            }}
            currencyInitial={otherAccount.currency.data.initial}
            currencyLabel={otherAccount.currency.data.label_unicode}
            currentUserIsGenerous={isOtherAccountIsAttractive}
            errors={errors}
            sex={otherProfile.sex}
            sendSuggestion={handleSuggestion}
            showWarn={message => {
              dispatch(displayWarnMessage({ info: message }));
            }}
            cannot_make_offer_reasons={accountModel.data.cannot_make_offer_reasons}
            buttonID="interestedOffers"
            isSuggestRequest={oneTapActiveAccount?.suggest}
            title={isOtherAccountIsAttractive ? 'Make an Offer!' : 'Request a Date!'}
            pageTitle={isOtherAccountIsAttractive ? 'Make Offer' : 'Request Date'}
            otherProfile={otherProfile}
          />
        </>
      )}
    </OfferActionContainer>
  );
};

export default React.memo(OneTapSwipeOfferActions);
